import { toast } from 'sonner';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import MachineCard from '@/components/common/MachineCard';
import MachineForm from './MachineForm';
import SectionHeader from '@/components/FullPageCarousel/SectionHeader';
import { IPostUploadData } from '@/redux/post/post';
import { useLazyGetCNCVariantsQuery } from '@/redux/cncVariant/cncVariant.api.slice';
import { CNCVariantI } from '@/redux/cncVariant/cncVariant';

type Machine = {
  cncVariantId: string;
  cncBrandName: string;
  cncModelName: string;
  cncVariantName: string;
};

const mapVariantToMachine = (
  variantId: string,
  variants: CNCVariantI[]
): Machine => {
  const variant = variants.find((v) => v._id === variantId);
  return {
    cncVariantId: variantId,
    cncBrandName: variant?.brandName || '',
    cncModelName: variant?.modelName || '',
    cncVariantName: variant?.name || '',
  };
};

export default function SupportedMachines({
  formData,
  setFormData,
}: {
  formData: IPostUploadData;
  setFormData: Dispatch<SetStateAction<IPostUploadData>>;
}) {
  const [getCNCVariants] = useLazyGetCNCVariantsQuery();

  const [originalMachine, setOriginalMachine] = useState<Machine>({
    cncVariantId: '',
    cncBrandName: '',
    cncModelName: '',
    cncVariantName: '',
  });

  const [supportedMachines, setSupportedMachines] = useState<Machine[]>([]);
  const [isOriginalDialogOpen, setIsOriginalDialogOpen] = useState(false);
  const [isSupportedDialogOpen, setIsSupportedDialogOpen] = useState(false);

  const handleSaveOriginalMachine = (machine: Omit<Machine, 'id'>) => {
    setSupportedMachines(
      supportedMachines.filter((m) => m.cncVariantId !== machine.cncVariantId)
    );
    if (
      supportedMachines.some((m) => m.cncVariantId === machine.cncVariantId)
    ) {
      toast.warning('Machine removed from supported machines');
    }
    setOriginalMachine({
      ...originalMachine,
      ...machine,
    });
    setFormData({
      ...formData,
      originalMachine: machine.cncVariantId,
    });
    setIsOriginalDialogOpen(false);
  };

  const handleSaveSupportedMachine = (machine: Omit<Machine, 'id'>) => {
    if (formData.originalMachine === machine.cncVariantId) {
      toast.error('Original machine cannot be added as supported machine');
      return;
    }
    if (
      supportedMachines.some((m) => m.cncVariantId === machine.cncVariantId)
    ) {
      toast.error('Machine already added');
      return;
    }
    setSupportedMachines([
      ...supportedMachines,
      {
        ...machine,
      },
    ]);
    setFormData({
      ...formData,
      supportedMachines: Array.from(
        new Set([...supportedMachines, machine].map((m) => m.cncVariantId))
      ),
    });
    setIsSupportedDialogOpen(false);
  };

  const handleDeleteMachine = (machineId: string) => {
    setSupportedMachines(
      supportedMachines.filter((machine) => machine.cncVariantId !== machineId)
    );

    setFormData({
      ...formData,
      supportedMachines: supportedMachines
        .filter((m) => m.cncVariantId !== machineId)
        .map((m) => m.cncVariantId),
    });
  };

  const isOriginalMachineConfigured = originalMachine?.cncVariantId;

  const getMachinesDataFromVariants = useCallback(async () => {
    const variants = await getCNCVariants({
      variantIdList: [formData.originalMachine, ...formData.supportedMachines],
    }).unwrap();

    const originalMachineData = mapVariantToMachine(
      formData.originalMachine,
      variants
    );
    setOriginalMachine(originalMachineData);

    const supportedMachinesData = formData.supportedMachines.map((variantId) =>
      mapVariantToMachine(variantId, variants)
    );
    setSupportedMachines(supportedMachinesData);
  }, [formData.originalMachine, formData.supportedMachines, getCNCVariants]);

  useEffect(() => {
    getMachinesDataFromVariants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Supported Machines"
        description="Enter supported machines for your project"
      />
      <div className="space-y-4">
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Machine Created On</h3>
            <Dialog
              open={isOriginalDialogOpen}
              onOpenChange={setIsOriginalDialogOpen}
            >
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  {isOriginalMachineConfigured ? 'Edit' : 'Set Up'}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Machine Created On</DialogTitle>
                </DialogHeader>
                <MachineForm
                  onSubmit={handleSaveOriginalMachine}
                  onCancel={() => setIsOriginalDialogOpen(false)}
                />
              </DialogContent>
            </Dialog>
          </div>

          {isOriginalMachineConfigured ? (
            <MachineCard
              id={originalMachine.cncVariantId}
              brand={originalMachine.cncBrandName}
              model={originalMachine.cncModelName}
              variant={originalMachine.cncVariantName}
            />
          ) : (
            <div className="p-8 border border-dashed rounded-md text-center text-muted-foreground">
              <p>No original machine configured</p>
            </div>
          )}
        </div>

        {/* Supported Machines Section */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Supported Machines</h3>
            <Dialog
              open={isSupportedDialogOpen}
              onOpenChange={setIsSupportedDialogOpen}
            >
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-2" /> Add Machine
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Add Supported Machine</DialogTitle>
                </DialogHeader>
                <MachineForm
                  onSubmit={handleSaveSupportedMachine}
                  onCancel={() => setIsSupportedDialogOpen(false)}
                />
              </DialogContent>
            </Dialog>
          </div>

          {supportedMachines.length > 0 ? (
            <div className="max-h-96 overflow-y-auto pr-4">
              <div className="flex flex-col gap-4">
                {supportedMachines.map((machine) => (
                  <MachineCard
                    key={machine.cncVariantId}
                    id={machine.cncVariantId}
                    brand={machine.cncBrandName}
                    model={machine.cncModelName}
                    variant={machine.cncVariantName}
                    onDelete={handleDeleteMachine}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="p-8 border border-dashed rounded-md text-center text-muted-foreground">
              <p>No supported machines added</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
